import { LandingPage } from '../../components'

const PrivacyPolicy = () => {
  return (
    <LandingPage
      title='Privacy Policy'
      description='Privacy policy for wowaudit.'
    >
      <div className='space-y-6'>
        <p className='text-sm text-muted-foreground'>
          Last updated: September 21, 2026
        </p>

        <section className='space-y-2 mt-8'>
          <h2>Introduction</h2>
          <p>
            wowaudit is a World of Warcraft guild management tool. This privacy
            policy explains what personal data we collect, how we use it, and
            your rights regarding that data when you use wowaudit.com, its
            subdomains, and the optional Google Sheets spreadsheet that guilds
            can copy and connect to their team.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Information we collect</h2>

          <h4>Account data</h4>
          <p>
            Accounts are created through Battle.net OAuth. When you log in we
            receive and store your Blizzard account ID, BattleTag, and a
            short-lived OAuth access token used to fetch your World of Warcraft
            characters.
          </p>

          <h4 className='mt-6'>Optional linked accounts</h4>
          <p>
            You can optionally link additional accounts for specific features.
            We store only what is needed to power those features:
          </p>
          <ul className='list-disc pl-5 space-y-1 text-sm'>
            <li>
              <strong>Google</strong>: Google account identifier and email
              address, used as a fallback login method and for email
              notifications. See{' '}
              <a
                href='#google-user-data'
                className='underline hover:opacity-80'
              >
                Google user data
              </a>{' '}
              below.
            </li>
            <li>
              <strong>Discord</strong>: Discord user ID, username, and avatar
              URL, used to send notifications and @mentions.
            </li>
            <li>
              <strong>Patreon</strong>: email address and membership
              status/tier, used to verify premium access.
            </li>
            <li>
              <strong>Warcraft Logs</strong>: WCL username and token, used to
              retrieve log data for your team.
            </li>
          </ul>

          <h4 className='mt-6'>World of Warcraft character data</h4>
          <p>
            We fetch your World of Warcraft characters from the Blizzard API
            using your OAuth token. This includes character names, realm, class,
            race, item level, achievements, and guild memberships. This data is
            regularly refreshed to keep audit information up to date.
          </p>

          <h4 className='mt-6'>Guild application data</h4>
          <p>
            If you apply to join a guild through wowaudit, you may submit
            additional information such as your age, country, role preference,
            free-text answers to application questions, and file attachments.
            This information is visible to the guild you apply to.
          </p>

          <h4 className='mt-6'>Settings and preferences</h4>
          <p>
            We store your account preferences, including BattleTag visibility
            settings (whether other members can see your full BattleTag),
            alt-character visibility, and notification preferences.
          </p>
        </section>

        <section id='google-user-data' className='space-y-2 mt-8'>
          <h2>Google user data</h2>
          <p>
            wowaudit uses Google APIs in two optional, separate integrations. We
            request only the permissions needed for the feature you are using.
            wowaudit&apos;s use of information received from Google APIs will
            adhere to the Google API Services User Data Policy, including the
            Limited Use requirements.
          </p>

          <h4 className='mt-6'>1. Google Sign-In on wowaudit.com</h4>
          <p>
            You can link a Google account in your wowaudit settings, or later
            sign in with that Google account as a fallback to Battle.net.
          </p>
          <ul className='list-disc pl-5 space-y-1 text-sm'>
            <li>
              <strong>Data accessed:</strong> your Google account identifier and
              email address. We do not request access to Gmail, Drive, Calendar,
              Contacts, or any Google files through this sign-in.
            </li>
            <li>
              <strong>How it is used:</strong> to match you to an existing
              wowaudit account and to send transactional email notifications
              (raid reminders and similar messages) unless you opt out. We do
              not use this data for advertising, analytics profiles, or credit
              decisions.
            </li>
            <li>
              <strong>Where it is stored:</strong> on our servers, associated
              with your wowaudit user record.
            </li>
          </ul>

          <h4 className='mt-6'>2. Google Sheets spreadsheet (Apps Script)</h4>
          <p>
            Guilds can make a copy of our master Google Sheet and authorize a
            bound Apps Script. The script is limited to that copied spreadsheet
            (<code>@OnlyCurrentDoc</code>). It does not access other files in
            your Google Drive or other spreadsheets.
          </p>
          <ul className='list-disc pl-5 space-y-1 text-sm'>
            <li>
              <strong>Data accessed:</strong> cells and structure of the copied
              spreadsheet only — for example the team key, refresh interval,
              spreadsheet version, and the sheets the script updates (such as
              raw data, summary, overview, vault, and raids).
            </li>
            <li>
              <strong>How it is used:</strong> to pull that team&apos;s wowaudit
              guild data into the sheet (&quot;Refresh data&quot;) and to apply
              layout/version updates we publish so copied sheets stay current
              (&quot;Update spreadsheet&quot;). Optional time-based triggers can
              refresh the sheet automatically. All of this is a user-facing
              guild-management feature in the spreadsheet itself.
            </li>
            <li>
              <strong>Data flow:</strong> the script downloads JSON from
              wowaudit servers and writes it into the sheet. It does not upload
              spreadsheet contents, Google account data, or other Google user
              data to wowaudit. The only identifier sent to our servers is the
              team key you paste into the sheet, used to select which
              already-exported guild dataset to fetch.
            </li>
            <li>
              <strong>Where it is stored:</strong> spreadsheet contents remain
              in your Google account. We do not store a copy of your Google
              Sheet on wowaudit servers.
            </li>
          </ul>

          <h4 className='mt-6'>Sharing and transfer of Google user data</h4>
          <p>
            We do not sell, rent, or trade Google user data. We do not share it
            with data brokers, advertisers, or for personalized advertising.
          </p>
          <ul className='list-disc pl-5 space-y-1 text-sm'>
            <li>
              <strong>Email address:</strong> may be sent to our email delivery
              provider (SendGrid) solely to deliver transactional notifications
              you requested or that are part of the service.
            </li>
            <li>
              <strong>Spreadsheet data:</strong> is not transferred to wowaudit
              or to other third parties by the Apps Script. It stays in the
              Google Sheet you copied.
            </li>
            <li>
              Other transfers occur only if required by law, to investigate
              abuse or security issues, or as part of a sale of the business
              with prior notice.
            </li>
          </ul>

          <h4 className='mt-6'>Protection of Google user data</h4>
          <p>
            Data in transit is protected with HTTPS. Google Sign-In tokens are
            stored server-side and are not exposed to the browser. Spreadsheet
            authorization is handled by Google for the bound script and is
            limited to the current document. Access to our servers is restricted
            to what is needed to operate the service.
          </p>

          <h4 className='mt-6'>Retention and deletion of Google user data</h4>
          <ul className='list-disc pl-5 space-y-1 text-sm'>
            <li>
              <strong>Google Sign-In:</strong> we keep your Google account
              identifier and email for as long as the Google account stays
              linked. Unlinking Google in your wowaudit account settings removes
              that Google data from our systems. You can also request deletion
              of your wowaudit account at{' '}
              <a
                href='mailto:privacy@wowaudit.com'
                className='underline hover:opacity-80'
              >
                privacy@wowaudit.com
              </a>
              .
            </li>
            <li>
              <strong>Spreadsheet:</strong> we do not retain your Google Sheet.
              You can stop the script by removing the wowaudit menu
              authorization in your Google Account (Google Account → Data &
              privacy → Third-party apps), deleting the script triggers, or
              deleting your copy of the sheet.
            </li>
          </ul>

          <h4 className='mt-6'>Limited Use and AI/ML</h4>
          <p>
            We use Google user data only to provide and improve the user-facing
            features described above (Google sign-in, email notifications, and
            the guild spreadsheet). We do not use Google user data, including
            aggregated or anonymized Google user data, to serve ads, to
            determine credit-worthiness, or to develop, improve, or train
            generalized AI or machine learning models. We do not transfer Google
            user data to third-party AI services.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>How we use your information</h2>
          <ul className='list-disc pl-5 space-y-1 text-sm'>
            <li>
              <strong>Authentication</strong>: to identify you when you log in
              and maintain your session.
            </li>
            <li>
              <strong>Character data</strong>: to compute gear summaries,
              in-game activity stats and other audit data for your team.
            </li>
            <li>
              <strong>Notifications</strong>: to send reminders and other
              messages to your email address or Discord account (unless you opt
              out).
            </li>
            <li>
              <strong>Premium verification</strong>: to check your Patreon
              membership status and grant premium features accordingly.
            </li>
            <li>
              <strong>Recruitment</strong>: to let guilds you apply to reach out
              to you.
            </li>
            <li>
              <strong>Spreadsheet</strong>: to let guilds view the same audit
              data in a Google Sheet they copied from our template.
            </li>
          </ul>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Cookies and local storage</h2>
          <p>
            We only use cookies that are essential for login and security. We do
            not use <strong>any</strong> tracking, analytics, or advertising
            cookies.
          </p>
          <p>
            In addition to cookies, we store UI preferences (such as selected
            filters, theme, and draft form data) in your browser&apos;s local
            storage. This data never leaves your device and is not sent to our
            servers.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Third-party services</h2>
          <p>
            We rely on the following third-party services to operate wowaudit.
            Each service has its own privacy policy governing how they handle
            data:
          </p>
          <ul className='list-disc pl-5 space-y-1 text-sm'>
            <li>
              <strong>Blizzard / Battle.net</strong>: authentication and World
              of Warcraft character data.
            </li>
            <li>
              <strong>Google</strong>: optional account linking for login and
              email notifications, and the optional Google Sheets Apps Script
              described above.
            </li>
            <li>
              <strong>Discord</strong>: optional account linking for
              notifications and bot commands.
            </li>
            <li>
              <strong>Patreon</strong>: optional account linking for premium
              membership verification.
            </li>
            <li>
              <strong>Warcraft Logs</strong>: optional account linking to
              retrieve log data.
            </li>
            <li>
              <strong>Raider.IO</strong>: Mythic+ score and character enrichment
              data fetched by our backend.
            </li>
            <li>
              <strong>SendGrid</strong>: transactional email delivery
              (notifications). If you linked Google, the recipient address may
              be your Google email.
            </li>
            <li>
              <strong>Sentry</strong>: server-side error monitoring to help us
              identify and fix bugs.
            </li>
            <li>
              <strong>Wowhead</strong>: item tooltip scripts loaded on most
              pages to display in-game item information.
            </li>
          </ul>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Data visibility and sharing</h2>
          <p>
            Your BattleTag visibility can be controlled in your account settings
            : you can choose to show your full BattleTag, hide the discriminator
            numbers, display a custom nickname, or hide it entirely.
          </p>
          <p>
            Teams that track your characters can see your character data, such
            as item level, gear, and in-game activity stats. You can control
            which teams can add your characters to their roster in your account
            settings.
          </p>
          <p>
            We do not sell, rent, or trade your personal data to any third
            parties for any purpose, including advertising platforms, data
            brokers, or information resellers.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Data retention</h2>
          <p>
            We retain your account data for as long as your account is active.
            OAuth access tokens are refreshed periodically and are not stored
            beyond their functional lifetime. Character data is refreshed from
            Blizzard&apos;s API and historical records may be kept to power
            audit history features. Google user data retention is described in{' '}
            <a href='#google-user-data' className='underline hover:opacity-80'>
              Google user data
            </a>
            .
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Your rights</h2>
          <p>
            You have the right to access, correct, or request deletion of your
            personal data. To exercise any of these rights, contact us at{' '}
            <a
              href='mailto:privacy@wowaudit.com'
              className='underline hover:opacity-80'
            >
              privacy@wowaudit.com
            </a>
            . We will respond within 30 days.
          </p>
          <p>
            You can unlink third-party accounts (Google, Discord, Patreon,
            Warcraft Logs) at any time from your account settings, which removes
            the associated data from our systems. You can revoke the spreadsheet
            script&apos;s access from your Google Account permissions at any
            time.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Security</h2>
          <p>
            We use industry-standard practices to protect your data, including
            encrypted connections (HTTPS), session tokens, and CSRF protection.
            OAuth access tokens are stored server-side and are never exposed to
            the browser.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Data controller</h2>
          <p>
            This site is operated by wowaudit, a sole proprietorship registered
            with the Dutch Chamber of Commerce under number 73736481. For any
            privacy-related questions you can contact us at{' '}
            <a
              href='mailto:privacy@wowaudit.com'
              className='underline hover:opacity-80'
            >
              privacy@wowaudit.com
            </a>
            .
          </p>
        </section>
      </div>
    </LandingPage>
  )
}

export default PrivacyPolicy
